import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Lock, Sparkles, RotateCw, Loader2 } from "lucide-react";
import { API } from "@/App";

/* --- Sub-components: keep main render simple --- */

const FlipCard = ({ card, flipped, onFlip }) => (
  <button
    type="button"
    onClick={onFlip}
    className="relative w-full h-[260px] rounded-2xl border-2 border-slate-200 bg-gradient-to-br from-white to-slate-50 hover:border-violet-300 transition-colors group cursor-pointer"
    style={{ perspective: "1000px" }}
    data-testid="preview-card"
    aria-label="Flip card"
  >
    <motion.div
      className="absolute inset-0 rounded-2xl"
      style={{ transformStyle: "preserve-3d" }}
      animate={{ rotateY: flipped ? 180 : 0 }}
      transition={{ duration: 0.5 }}
    >
      <div
        className="absolute inset-0 flex items-center justify-center p-8 text-center"
        style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
      >
        <div>
          <p className="text-2xl md:text-3xl font-bold text-slate-900 leading-tight">
            {card.front}
          </p>
          <p className="text-xs text-slate-400 mt-6 flex items-center justify-center gap-1">
            <RotateCw className="w-3 h-3" />
            Tap or press space to flip
          </p>
        </div>
      </div>
      <div
        className="absolute inset-0 flex items-center justify-center p-8 text-center bg-violet-50 rounded-2xl"
        style={{
          backfaceVisibility: "hidden",
          WebkitBackfaceVisibility: "hidden",
          transform: "rotateY(180deg)",
        }}
      >
        <p className="text-base md:text-lg text-slate-800 leading-relaxed">
          {card.back}
        </p>
      </div>
    </motion.div>
  </button>
);

const NavigationBar = ({ index, cards, onPrev, onNext, onUnlock, termCount }) => {
  const onLast = cards.length > 0 && index === cards.length - 1;
  return (
    <div className="flex items-center justify-between mt-5">
      <Button
        variant="outline"
        onClick={onPrev}
        disabled={index === 0}
        className="rounded-full"
        data-testid="preview-prev-btn"
      >
        <ChevronLeft className="w-4 h-4 mr-1" /> Prev
      </Button>

      <div className="flex items-center gap-1.5">
        {cards.map((c, i) => (
          <div
            key={`dot-${c.front}`}
            className={`w-1.5 h-1.5 rounded-full transition-colors ${
              i === index ? "bg-violet-600" : "bg-slate-200"
            }`}
          />
        ))}
      </div>

      {onLast ? (
        <Button
          onClick={onUnlock}
          className="rounded-full bg-violet-600 hover:bg-violet-700 text-white"
          data-testid="preview-unlock-btn"
        >
          <Sparkles className="w-4 h-4 mr-1" />
          Unlock all {termCount}
        </Button>
      ) : (
        <Button
          onClick={onNext}
          className="rounded-full bg-violet-600 hover:bg-violet-700 text-white"
          data-testid="preview-next-btn"
        >
          Next <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      )}
    </div>
  );
};

const LockedStrip = ({ count }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 10 }}
    className="mt-5 rounded-xl border border-violet-200 bg-gradient-to-r from-violet-50 to-amber-50 px-4 py-3 flex items-center gap-3"
    data-testid="preview-locked-strip"
  >
    <Lock className="w-4 h-4 text-violet-600 shrink-0" />
    <p className="text-sm text-slate-700 flex-1">
      <span className="font-semibold">{count.toLocaleString()} more cards</span>{" "}
      waiting — sign up free to study the full set with spaced repetition.
    </p>
  </motion.div>
);

/**
 * Public flashcard preview modal — shows the first 5 cards of a curated
 * popular set without requiring sign-in. After the last card, a paywall-
 * style "Unlock all X terms" CTA invites the visitor to sign up.
 *
 * This is the primary acquisition funnel: visitors taste real value first.
 */
const FlashcardPreviewModal = ({ open, setId, onClose, onUnlock }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);

  // Reset state when a new set is opened
  useEffect(() => {
    if (open && setId) {
      setData(null);
      setError(null);
      setIndex(0);
      setFlipped(false);
      setLoading(true);
      axios
        .get(`${API}/public/flashcard-sets/${setId}/preview`)
        .then((res) => setData(res.data))
        .catch(() => setError("Could not load preview. Please try again."))
        .finally(() => setLoading(false));
    }
  }, [open, setId]);

  const cards = data?.preview_cards || [];
  const total = cards.length;
  const card = cards[index];

  const next = useCallback(() => {
    setFlipped(false);
    setIndex((i) => Math.min(i + 1, total - 1));
  }, [total]);

  const prev = useCallback(() => {
    setFlipped(false);
    setIndex((i) => Math.max(i - 1, 0));
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        setFlipped((f) => !f);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="sm:max-w-2xl p-0 gap-0 overflow-hidden bg-white"
        data-testid="flashcard-preview-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100">
          <DialogTitle className="text-xl font-bold text-slate-900 pr-6">
            {data?.title || "Preview"}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {data ? (
              <>
                Free preview · {Math.min(index + 1, total)} of {total} cards ·{" "}
                {data.term_count.toLocaleString()} total terms in this set
              </>
            ) : (
              "Loading preview…"
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-6 min-h-[360px] flex flex-col">
          {loading && (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
            </div>
          )}

          {error && (
            <div className="flex-1 flex items-center justify-center text-sm text-red-500">
              {error}
            </div>
          )}

          {!loading && !error && card && (
            <>
              <FlipCard
                card={card}
                flipped={flipped}
                onFlip={() => setFlipped((f) => !f)}
              />

              <NavigationBar
                index={index}
                cards={cards}
                onPrev={prev}
                onNext={next}
                onUnlock={onUnlock}
                termCount={data?.term_count}
              />

              <AnimatePresence>
                {data?.locked_count > 0 && index >= total - 2 && (
                  <LockedStrip count={data.locked_count} />
                )}
              </AnimatePresence>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FlashcardPreviewModal;
